import React, { useEffect, useMemo, useState } from "react";
import "./FinancePage.css";
import { listBillingInvoices } from "../../api/paymentsApi";
import SearchInput from "../ui/SearchInput";
import InvoiceDetailModal from "./InvoiceDetailModal";

const money = (value) => {
  // No value is "—", never a fabricated $0.00 (a null balance is unknown, not zero).
  if (value === null || value === undefined || value === "") return "—";
  const n = Number(value);
  if (!Number.isFinite(n)) return "—";
  return `$${n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const STATUS_CLASS = {
  paid: "success",
  issued: "warn",
  partially_paid: "warn",
  overdue: "danger",
  draft: "",
  void: "",
};

const STATUS_LABEL = {
  paid: "Paid",
  issued: "Issued",
  partially_paid: "Partial",
  overdue: "Overdue",
  draft: "Draft",
  void: "Void",
};

const toRows = (value) =>
  Array.isArray(value) ? value : Array.isArray(value?.results) ? value.results : [];

export default function InvoicesPage() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [query, setQuery] = useState("");
  const [invoices, setInvoices] = useState([]);
  const [selected, setSelected] = useState(null);

  const loadInvoices = async () => {
    setLoading(true);
    setError("");
    try {
      const res = await listBillingInvoices({ page_size: 200 });
      setInvoices(toRows(res?.data));
    } catch (e) {
      setError(e?.message || "Unable to load invoices.");
      setInvoices([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadInvoices();
  }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return invoices;
    return invoices.filter((inv) => {
      const hay = [
        inv.invoice_number,
        inv.customer_name,
        inv.status,
        String(inv.total || ""),
      ]
        .map((v) => String(v || "").toLowerCase())
        .join(" ");
      return hay.includes(q);
    });
  }, [invoices, query]);

  const stats = useMemo(() => {
    let outstanding = 0;
    let paid30d = 0;
    let overdue = 0;
    const now = new Date();
    const days30 = 30 * 24 * 60 * 60 * 1000;
    invoices.forEach((inv) => {
      const balance = Number(inv.balance_due || 0);
      const total = Number(inv.total || 0);
      const status = (inv.status || "").toLowerCase();
      if (status === "paid") {
        // `|| 0` made a missing paid_at read as 1970 and silently dropped the invoice from the 30-day total.
        const paidAt = inv.paid_at ? new Date(inv.paid_at) : null;
        if (paidAt && !Number.isNaN(paidAt.getTime()) && now - paidAt <= days30) {
          paid30d += total;
        }
      } else if (status === "overdue") {
        overdue += balance;
      } else if (balance > 0) {
        outstanding += balance;
      }
    });
    return { outstanding, paid30d, overdue, count: invoices.length };
  }, [invoices]);

  return (
    <div className="finance-page">
      <header className="finance-head">
        <div>
          <h2>Invoices</h2>
          <p className="finance-muted">Track issued invoices, payments, and collection status.</p>
        </div>
      </header>

      <div style={{ marginTop: 10, display: "flex", gap: 10, alignItems: "center" }}>
        <div style={{ flex: 1 }}>
          <SearchInput
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search by invoice number, customer, status..."
          />
        </div>
        <button
          className="finance-btn secondary"
          type="button"
          onClick={() => { setQuery(""); setSelected(null); }}
        >
          Clear
        </button>
      </div>

      <section className="finance-stat-grid">
        <div className="finance-stat">
          <div className="label">Outstanding</div>
          <div className="value">{money(stats.outstanding)}</div>
        </div>
        <div className="finance-stat">
          <div className="label">Paid (30d)</div>
          <div className="value">{money(stats.paid30d)}</div>
        </div>
        <div className="finance-stat">
          <div className="label">Overdue</div>
          <div className="value">{money(stats.overdue)}</div>
        </div>
        <div className="finance-stat">
          <div className="label">Total Invoices</div>
          <div className="value">{stats.count}</div>
        </div>
      </section>

      <section className="finance-card">
        <div className="finance-card-head">
          <h3>Invoices</h3>
        </div>
        {loading && <div className="finance-muted" style={{ padding: 14 }}>Loading...</div>}
        {error && <div className="finance-muted" style={{ padding: 14, color: "var(--danger)" }}>{error}</div>}
        {!loading && !error && filtered.length === 0 && (
          <div className="finance-muted" style={{ padding: 24, textAlign: "center" }}>
            No invoices yet. Invoices are created automatically when quotes are paid.
          </div>
        )}
        {filtered.length > 0 && (
          <div className="finance-table" style={{ "--finance-columns": "0.8fr 1.4fr 0.8fr 0.8fr 0.8fr 0.7fr" }}>
            <div className="finance-row head">
              <div>Invoice</div>
              <div>Customer</div>
              <div>Total</div>
              <div>Paid</div>
              <div>Balance</div>
              <div>Status</div>
            </div>
            {filtered.map((inv) => (
              <div
                key={inv.id}
                className="finance-row"
                role="button"
                tabIndex={0}
                onClick={() => setSelected(inv)}
                onKeyDown={(e) => e.key === "Enter" && setSelected(inv)}
                style={{ cursor: "pointer" }}
              >
                <div className="mono">{inv.invoice_number || "—"}</div>
                <div>{inv.customer_name || "—"}</div>
                <div>{money(inv.total)}</div>
                <div>{money(inv.amount_paid)}</div>
                <div>{money(inv.balance_due)}</div>
                <div>
                  <span className={`finance-pill ${STATUS_CLASS[inv.status] || ""}`}>
                    {STATUS_LABEL[inv.status] || inv.status || "—"}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      {selected && (
        <InvoiceDetailModal
          invoiceId={selected.id}
          onClose={() => setSelected(null)}
          onPaymentRecorded={loadInvoices}
        />
      )}
    </div>
  );
}
